import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { KeyRound, MailCheck } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const emailFromLink = params.get("email") || "";
  const isReset = !!token;
  const nav = useNavigate();

  const [email, setEmail] = useState(emailFromLink);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const requestReset = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const submitNew = async (e) => {
    e.preventDefault();
    if (password !== confirm) { toast.error("Passwords do not match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { email: emailFromLink, token, password });
      toast.success("Password updated — please sign in");
      nav("/login");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#09090B] text-white flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md" data-testid="reset-password-page">
        <div className="w-12 h-12 rounded-2xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-400 mb-5">
          {isReset ? <KeyRound className="w-6 h-6" /> : <MailCheck className="w-6 h-6" />}
        </div>

        {isReset ? (
          <>
            <div className="text-[11px] uppercase tracking-[0.28em] text-amber-400 font-adult-mono mb-3">New password</div>
            <h1 className="font-adult-heading text-3xl font-semibold tracking-tight">Choose a new password</h1>
            <form onSubmit={submitNew} className="mt-8 space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] font-adult-mono text-zinc-400">New password</Label>
                <Input data-testid="reset-password-input" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="mt-2 bg-zinc-950 border-zinc-800 h-11" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] font-adult-mono text-zinc-400">Confirm password</Label>
                <Input data-testid="reset-confirm-input" type="password" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-2 bg-zinc-950 border-zinc-800 h-11" />
              </div>
              <Button type="submit" data-testid="reset-submit-btn" disabled={busy} className="w-full bg-amber-500 hover:bg-amber-400 text-black font-semibold rounded-full py-6">
                {busy ? "Saving…" : "Update password"}
              </Button>
            </form>
            <Link to="/login" className="inline-block mt-6 text-zinc-500 hover:text-zinc-300 text-sm">← Back to sign in</Link>
          </>
        ) : sent ? (
          <div data-testid="reset-sent-message">
            <h1 className="font-adult-heading text-3xl font-semibold tracking-tight">Check your inbox</h1>
            <p className="mt-4 text-zinc-400">If an account exists for <span className="text-zinc-200">{email}</span>, we've sent a link to reset your password. It expires in 30 minutes.</p>
            <Link to="/login" className="inline-block mt-8 text-amber-400 hover:text-amber-300 text-sm">← Back to sign in</Link>
          </div>
        ) : (
          <>
            <div className="text-[11px] uppercase tracking-[0.28em] text-amber-400 font-adult-mono mb-3">Password reset</div>
            <h1 className="font-adult-heading text-3xl font-semibold tracking-tight">Forgot your password?</h1>
            <p className="mt-3 text-sm text-zinc-400">Enter your account email and we'll send you a secure reset link.</p>
            <form onSubmit={requestReset} className="mt-8 space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] font-adult-mono text-zinc-400">Email</Label>
                <Input data-testid="forgot-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2 bg-zinc-950 border-zinc-800 h-11" />
              </div>
              <Button type="submit" data-testid="forgot-submit-btn" disabled={busy} className="w-full bg-amber-500 hover:bg-amber-400 text-black font-semibold rounded-full py-6">
                {busy ? "Sending…" : "Send reset link"}
              </Button>
            </form>
            <Link to="/login" className="inline-block mt-6 text-zinc-500 hover:text-zinc-300 text-sm">← Back to sign in</Link>
          </>
        )}
      </div>
    </div>
  );
}
